import {
  Schedule,
  CreateScheduleRequest,
  UpdateScheduleRequest,
  ScheduleListResponse,
  ScheduleWithValvesResponse,
  ScheduleValidationResult,
  ValveInfo
} from '@/types/schedule';
import { apiClient } from '@/services/apiClient';
import timezoneService from '@/services/timezoneService';

class ScheduleApiService {
  private api = apiClient;

  // Simple Schedule CRUD operations
  async getSchedules(activeOnly: boolean = false): Promise<ScheduleListResponse> {
    const params = activeOnly ? '?active_only=true' : '';
    const response = await this.api.get<ScheduleListResponse>(`/schedules/${params}`);
    
    // Map field names for consistency
    if (response.schedules) {
      response.schedules = response.schedules.map((schedule: any) => this.mapScheduleFields(schedule));
    }
    
    return response;
  }

  async getSchedulesWithValves(activeOnly: boolean = false): Promise<ScheduleWithValvesResponse[]> {
    const params = activeOnly ? '?active_only=true' : '';
    const response = await this.api.get<ScheduleWithValvesResponse[]>(`/schedules/with-valves${params}`);
    
    // Map field names for consistency
    if (Array.isArray(response)) {
      return response.map((schedule: any) => this.mapScheduleWithValvesFields(schedule));
    }
    
    return response;
  }

  async getSchedule(scheduleId: string): Promise<Schedule> {
    const response = await this.api.get<Schedule>(`/schedules/${scheduleId}`);
    
    // Map field names for consistency
    return this.mapScheduleFields(response);
  }

  async createSchedule(scheduleData: CreateScheduleRequest): Promise<Schedule> {
    const response = await this.api.post<Schedule>('/schedules', scheduleData);
    
    // Map field names for consistency
    return this.mapScheduleFields(response);
  }

  async updateSchedule(scheduleId: string, scheduleData: UpdateScheduleRequest): Promise<Schedule> {
    const response = await this.api.put<Schedule>(`/schedules/${scheduleId}`, scheduleData);
    
    // Map field names for consistency
    return this.mapScheduleFields(response);
  }

  async deleteSchedule(scheduleId: string): Promise<{ message: string; schedule_id: string }> {
    return this.api.delete<{ message: string; schedule_id: string }>(`/schedules/${scheduleId}`);
  }

  async toggleSchedule(scheduleId: string, active: boolean): Promise<{ message: string; schedule_id: string; active: boolean }> {
    return this.api.patch<{ message: string; schedule_id: string; active: boolean }>(`/schedules/${scheduleId}/toggle`, { active });
  }

  // Schedule validation
  async validateSchedule(scheduleData: CreateScheduleRequest): Promise<ScheduleValidationResult> {
    return this.api.post<ScheduleValidationResult>('/schedules/validate', scheduleData);
  }

  async validateExistingSchedule(scheduleId: string): Promise<ScheduleValidationResult> {
    return this.api.get<ScheduleValidationResult>(`/schedules/${scheduleId}/validate`);
  }

  // Valve information helpers
  async getValves(): Promise<ValveInfo[]> {
    // This would typically come from devices API, but we'll implement it here for convenience
    const response = await this.api.get<{ valves: ValveInfo[] }>('/valves');
    
    // Map field names for consistency
    if (response.valves && Array.isArray(response.valves)) {
      return response.valves.map((valve: any) => this.mapValveFields(valve));
    }
    
    return [];
  }

  async getValvesForDevice(deviceEui: string): Promise<ValveInfo[]> {
    const response = await this.api.get<ValveInfo[]>(`/devices/${deviceEui}/valves`);
    
    // Map field names for consistency
    if (Array.isArray(response)) {
      return response.map((valve: any) => this.mapValveFields(valve));
    }
    
    return [];
  }

  // Utility methods
  formatScheduleTime(timeString: string): string {
    // Convert "HH:mm" to more readable format if needed
    return timeString;
  }

  formatDuration(minutes: number): string {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    
    if (hours === 0) {
      return `${mins}m`;
    } else if (mins === 0) {
      return `${hours}h`;
    } else {
      return `${hours}h ${mins}m`;
    }
  }

  formatDays(days: string[]): string {
    if (days.length === 1 && days[0] === 'Every Day') {
      return 'Every Day';
    }
    
    return days.join(', ');
  }

  // Field mapping utility to handle both snake_case and camelCase
  private mapScheduleFields(data: any): Schedule {
    return {
      id: data.id,
      name: data.name,
      startTime: data.startTime || data.start_time,
      durationMinutes: data.durationMinutes || data.duration_minutes,
      valveIds: data.valveIds || data.valve_ids,
      active: data.active,
      days: data.days,
      userId: data.userId || data.user_id,
      createdAt: data.createdAt || data.created_at,
      updatedAt: data.updatedAt || data.updated_at,
    };
  }

  // Field mapping utility for valves to handle both snake_case and camelCase
  private mapValveFields(data: any): ValveInfo {
    return {
      id: data.id,
      deviceId: data.deviceId || data.device_id?.toString(),
      deviceName: data.deviceName || data.name || `Device ${data.device_id}`,
      alias: data.alias || data.name, // Use alias if available, fallback to name
      valveNumber: data.valveNumber || data.valve_number,
      location: data.location,
    };
  }

  // Field mapping utility for schedules with valves to handle both snake_case and camelCase
  private mapScheduleWithValvesFields(data: any): ScheduleWithValvesResponse {
    const schedule = this.mapScheduleFields(data);
    const valves = data.valves ? data.valves.map((valve: any) => this.mapValveFields(valve)) : [];
    
    return {
      ...schedule,
      valves,
    };
  }

  isScheduleActiveNow(schedule: Schedule): boolean {
    if (!schedule.active) {
      return false;
    }

    const now = new Date();
    const localStartTime = timezoneService.utcTimeToLocal(schedule.startTime);
    const currentDay = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][now.getDay()];

    // Check if today is in schedule days
    const isTodayScheduled = schedule.days.includes('Every Day') || schedule.days.includes(currentDay);
    if (!isTodayScheduled) {
      return false;
    }

    // Check if current time is within the schedule duration
    if (!localStartTime) {
      return false;
    }

    const [startHours, startMinutes] = localStartTime.split(':').map(Number);
    const startTimeMinutes = startHours * 60 + startMinutes;
    const endTimeMinutes = startTimeMinutes + schedule.durationMinutes;
    const currentTimeMinutes = now.getHours() * 60 + now.getMinutes();

    return currentTimeMinutes >= startTimeMinutes && currentTimeMinutes < endTimeMinutes;
  }

  getNextScheduledTime(schedule: Schedule): Date | null {
    if (!schedule.active) {
      return null;
    }

    const now = new Date();
    const localStartTime = timezoneService.utcTimeToLocal(schedule.startTime);
    const currentTimeMinutes = now.getHours() * 60 + now.getMinutes();
  
    // Parse start time
    if (!localStartTime) {
      return null;
    }

    const [startHours, startMinutes] = localStartTime.split(':').map(Number);
    const startTimeMinutes = startHours * 60 + startMinutes;

    // Find the next scheduled day
    for (let i = 0; i < 7; i++) {
      const checkDate = new Date(now);
      checkDate.setDate(now.getDate() + i);
      const checkDay = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][checkDate.getDay()];
      
      if (schedule.days.includes('Every Day') || schedule.days.includes(checkDay)) {
        const isToday = i === 0;
        const scheduleTimeMinutes = isToday ? startTimeMinutes : startTimeMinutes;
        
        if (!isToday || currentTimeMinutes < scheduleTimeMinutes) {
          checkDate.setHours(Math.floor(scheduleTimeMinutes / 60));
          checkDate.setMinutes(scheduleTimeMinutes % 60);
          checkDate.setSeconds(0);
          checkDate.setMilliseconds(0);
          return checkDate;
        }
      }
    }

    return null; // No upcoming schedule this week
  }
}

export default new ScheduleApiService();